import { shallowEqual } from 'react-redux'

import { Col, Row, Upload } from 'antd'
import { Icon } from '@iconify/react'
import ImgCrop from 'antd-img-crop'
import type { FormikErrors, FormikValues } from 'formik'
import { Form, Formik } from 'formik'

import { useAuth } from 'app/providers/auth/AuthProvider'
import { useNotification } from 'app/providers'

import { Button } from 'common/components/Button/Button'
import { FormControl } from 'common/components/FormItems/FormControl/FormControl'
import { Show } from 'common/components/Show/Show'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import {
  BUTTON_CONSTANTS,
  BUTTON_MODIFIER,
  BUTTON_PROPORTION,
  BUTTON_SEVERITY,
} from 'common/constants/buttonConstants'
import { FORM_CONTROL_TYPE } from 'common/constants/formControlConstants'
import { INPUT_TYPES } from 'common/constants/inputConstants'
import { UPLOAD_CONSTANTS } from 'common/constants/uploadConstants'
import { UtilService } from 'common/services/utilService'
import { useFetchCountriesQuery, useFetchStatesQuery } from 'common/store/api/rootApi'

import { USA_COUNTRY_ID } from 'features/SettingsOld/constants/settingsAddCard'
import { SETTINGS_PROFILE_KEYS } from 'features/SettingsOld/constants/settingsProfileKeys'
import { SETTINGS_PROFILE_LABELS } from 'features/SettingsOld/constants/settingsProfileLabels'
import { SETTINGS_PROFILE_DOCTOR_SCHEMA } from 'features/SettingsOld/schemas/settingsProfileDoctorSchema'
import { SETTINGS_PROFILE_STAFF_SCHEMA } from 'features/SettingsOld/schemas/settingsProfileStaffSchema'
import {
  useFetchProfileQuery,
  useRefreshDoctorEmrTokenMutation,
  useUpdateImageMutation,
  useUpdateProfileMutation,
  useUploadImageMutation,
} from 'features/SettingsOld/state/api/settingsApi'
import { WidgetPhoto } from 'features/Widget/components/WidgetPhoto/WidgetPhoto'

import { useApiResponse } from 'common/hooks/useApiResponse'
import { SaveButton } from 'features/Profile/components/SaveButton/SaveButton'
import styles from './ProfileForm.module.scss'

const { clearObjectByEmptyValues, getInitials } = UtilService
const { fileSizeInMB } = UtilService

type Props = {
  onDeleteAvatar: () => void
}

export const ProfileForm = ({ onDeleteAvatar }: Props) => {
  const { setNotification } = useNotification()
  const { isDoctor, isSuperAdminAsDoctor, hasEmrEnabled } = useAuth()
  const { processApiResponse } = useApiResponse()

  const { data: states } = useFetchStatesQuery()
  const { data: countries } = useFetchCountriesQuery()

  const { data, isFetching: isFetchingProfile } = useFetchProfileQuery()
  const [updateProfile, { isLoading: isUpdatingProfile }] = useUpdateProfileMutation()
  const [refreshDoctorEmrToken, { isLoading: isRefreshingOeToken }] =
    useRefreshDoctorEmrTokenMutation()

  const [uploadImage] = useUploadImageMutation()
  const [updateImage] = useUpdateImageMutation()

  const imageUrl = data?.profile?.image?.url
  const canRefreshOeToken = isSuperAdminAsDoctor && hasEmrEnabled

  const handleSubmitProfileForm = async (profileData: FormikValues): Promise<void> => {
    const response = await updateProfile({
      ...clearObjectByEmptyValues(profileData),
    })

    processApiResponse(response, {
      error: 'Update profile error',
      success: 'Update profile success',
    })
  }

  const onUpdateAvatar = async ({ file }: { file: File }): Promise<void> => {
    if (!file.type.includes('image')) {
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        title: UPLOAD_CONSTANTS.TYPE_ERROR_TITLE,
        description: UPLOAD_CONSTANTS.TYPE_ERROR_DESCRIPTION,
      })
      return
    }
    if (fileSizeInMB(file.size) >= 10) {
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        title: UPLOAD_CONSTANTS.SIZE_ERROR_TITLE,
        description: UPLOAD_CONSTANTS.SIZE_ERROR_DESCRIPTION,
      })
      return
    }
    const { data } = await uploadImage(file)

    const response = await updateImage(data.file)
    processApiResponse(response, {
      title: UPLOAD_CONSTANTS.SIZE_ERROR_TITLE,
      error: UPLOAD_CONSTANTS.SIZE_ERROR_DESCRIPTION,
      success: 'Update avatar success',
    })
  }

  const handleRefreshOeToken = async (): Promise<void> => {
    try {
      const response = await refreshDoctorEmrToken().unwrap()

      if (response?.status === false) {
        setNotification({
          title: 'Error',
          description: response.message || 'Failed to refresh OE token',
          type: ALERT_CONSTANTS.ERROR,
        })
        return
      }

      setNotification({
        description: response?.message || 'OE token refreshed successfully',
        type: ALERT_CONSTANTS.SUCCESS,
      })
    } catch (error: unknown) {
      const errorData = (error as { data?: string[] | string })?.data
      const errorMessage = Array.isArray(errorData)
        ? errorData[0]
        : errorData || 'Failed to refresh OE token'

      setNotification({
        title: 'Error',
        description: errorMessage,
        type: ALERT_CONSTANTS.ERROR,
      })
    }
  }

  const initials = getInitials(data?.profile?.first_name, data?.profile?.last_name)
  const isFetching = isFetchingProfile || isUpdatingProfile

  return (
    <div className={styles.parent}>
      <Formik
        enableReinitialize
        validateOnChange={false}
        onSubmit={(values: FormikValues) => handleSubmitProfileForm(values)}
        initialValues={data?.profile || {}}
        validationSchema={
          isDoctor ? SETTINGS_PROFILE_DOCTOR_SCHEMA : SETTINGS_PROFILE_STAFF_SCHEMA
        }>
        {({
          values,
          setErrors,
          dirty,
        }: {
          values: FormikValues
          setErrors: (errors: FormikErrors<FormikValues>) => void
          dirty: boolean
        }): React.JSX.Element => {
          if (shallowEqual(values, data?.profile)) {
            setErrors({})
          }
          return (
            <Form>
              <div className={styles.parentFormItems}>
                <Row align='middle' gutter={[16, 16]}>
                  <Col>
                    <h2 className={styles.parentFormDescription}>
                      {SETTINGS_PROFILE_LABELS.PROFILE_PHOTO}
                    </h2>
                  </Col>
                  <Col>
                    <div className={styles.parentFormImageItems}>
                      <WidgetPhoto initials={initials} hasShadow={false} src={imageUrl} />
                      <ImgCrop aspect={1} minZoom={0} maxZoom={5} cropShape='round'>
                        <Upload
                          showUploadList={false}
                          customRequest={onUpdateAvatar as any}
                          accept='image/png, image/jpeg'>
                          <Button
                            modifier={BUTTON_MODIFIER.SECONDARY}
                            proportion={BUTTON_PROPORTION.SMALL}>
                            {imageUrl ? BUTTON_CONSTANTS.CHANGE : BUTTON_CONSTANTS.UPLOAD}
                          </Button>
                        </Upload>
                      </ImgCrop>
                      <Show when={imageUrl}>
                        <Button
                          onClick={onDeleteAvatar}
                          modifier={BUTTON_MODIFIER.DEFAULT}
                          className={styles.parentFormRemoveButton}
                          proportion={BUTTON_PROPORTION.SMALL}
                          severity={BUTTON_SEVERITY.DANGER_OUTLINE}>
                          {BUTTON_CONSTANTS.REMOVE}
                        </Button>
                      </Show>
                    </div>
                  </Col>
                </Row>
                <div>
                  <h2 className={styles.parentFormDescription}>
                    {SETTINGS_PROFILE_LABELS.USER_DETAILS}
                  </h2>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12} xxl={6}>
                      <FormControl
                        required
                        isLoading={isFetching}
                        type={INPUT_TYPES.TEXT}
                        control={FORM_CONTROL_TYPE.INPUT}
                        name={SETTINGS_PROFILE_KEYS.FIRST_NAME}
                        label={SETTINGS_PROFILE_LABELS.FIRST_NAME}
                        tabIndex={1}
                      />
                    </Col>
                    <Col xs={24} md={12} xxl={6}>
                      <FormControl
                        required
                        isLoading={isFetching}
                        type={INPUT_TYPES.TEXT}
                        control={FORM_CONTROL_TYPE.INPUT}
                        name={SETTINGS_PROFILE_KEYS.LAST_NAME}
                        label={SETTINGS_PROFILE_LABELS.LAST_NAME}
                        tabIndex={2}
                      />
                    </Col>
                    <Col xs={24} md={12} xxl={6}>
                      <FormControl
                        required
                        type={INPUT_TYPES.EMAIL}
                        isLoading={isFetching}
                        control={FORM_CONTROL_TYPE.INPUT}
                        name={SETTINGS_PROFILE_KEYS.EMAIL}
                        label={SETTINGS_PROFILE_LABELS.EMAIL}
                        tabIndex={3}
                      />
                    </Col>
                    <Col xs={24} md={12} xxl={6}>
                      <FormControl
                        required
                        type={INPUT_TYPES.TEXT}
                        isLoading={isFetching}
                        control={FORM_CONTROL_TYPE.PHONE}
                        name={SETTINGS_PROFILE_KEYS.PHONE}
                        label={SETTINGS_PROFILE_LABELS.PHONE}
                        tabIndex={4}
                      />
                    </Col>
                    {isDoctor && (
                      <Col xs={24} md={12} xxl={6}>
                        <FormControl
                          required
                          type={INPUT_TYPES.TEXT}
                          isLoading={isFetching}
                          control={FORM_CONTROL_TYPE.PHONE}
                          name={SETTINGS_PROFILE_KEYS.OFFICE_PHONE}
                          label={SETTINGS_PROFILE_LABELS.OFFICE_PHONE}
                        />
                      </Col>
                    )}
                  </Row>
                </div>
                <Show when={isDoctor}>
                  <div>
                    <h2 className={styles.parentFormDescription}>
                      {SETTINGS_PROFILE_LABELS.MAILING_ADDRESS}
                    </h2>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12} xxl={6}>
                        <FormControl
                          type={INPUT_TYPES.TEXT}
                          isLoading={isFetching}
                          control={FORM_CONTROL_TYPE.INPUT}
                          name={SETTINGS_PROFILE_KEYS.MAILING_LINE1}
                          label={SETTINGS_PROFILE_LABELS.MAILING_LINE1}
                        />
                      </Col>
                      <Col xs={24} md={12} xxl={6}>
                        <FormControl
                          type={INPUT_TYPES.TEXT}
                          isLoading={isFetching}
                          control={FORM_CONTROL_TYPE.INPUT}
                          name={SETTINGS_PROFILE_KEYS.MAILING_LINE2}
                          label={SETTINGS_PROFILE_LABELS.MAILING_LINE2}
                        />
                      </Col>
                      <Col xs={24} md={8} lg={6} xxl={4}>
                        <FormControl
                          type={INPUT_TYPES.TEXT}
                          isLoading={isFetching}
                          control={FORM_CONTROL_TYPE.INPUT}
                          name={SETTINGS_PROFILE_KEYS.MAILING_CITY}
                          label={SETTINGS_PROFILE_LABELS.CITY}
                        />
                      </Col>
                      <Col xs={24} md={12} lg={4} xxl={3}>
                        <FormControl
                          options={states || []}
                          type={INPUT_TYPES.TEXT}
                          isLoading={isFetching}
                          control={FORM_CONTROL_TYPE.SELECT}
                          name={SETTINGS_PROFILE_KEYS.MAILING_STATE}
                          label={SETTINGS_PROFILE_LABELS.STATE}
                        />
                      </Col>

                      <Col xs={24} md={8} lg={6} xxl={4}>
                        <FormControl
                          type={INPUT_TYPES.TEXT}
                          isLoading={isFetching}
                          control={FORM_CONTROL_TYPE.INPUT}
                          name={SETTINGS_PROFILE_KEYS.MAILING_ZIPCODE}
                          label={SETTINGS_PROFILE_LABELS.ZIP_CODE}
                        />
                      </Col>
                    </Row>
                  </div>
                  <div>
                    <h2 className={styles.parentFormDescription}>
                      {SETTINGS_PROFILE_LABELS.PROFESSIONAL_DETAILS}
                    </h2>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12} lg={8} xxl={6}>
                        <FormControl
                          type={INPUT_TYPES.TEXT}
                          isLoading={isFetching}
                          control={FORM_CONTROL_TYPE.INPUT}
                          name={SETTINGS_PROFILE_KEYS.SALUTATION}
                          label={SETTINGS_PROFILE_LABELS.SALUTATION}
                        />
                      </Col>
                      <Col xs={24} md={12} lg={8} xxl={6}>
                        <FormControl
                          type={INPUT_TYPES.TEXT}
                          isLoading={isFetching}
                          control={FORM_CONTROL_TYPE.INPUT}
                          name={SETTINGS_PROFILE_KEYS.DISPLAY_NAME}
                          label={SETTINGS_PROFILE_LABELS.DISPLAY_NAME}
                        />
                        <span className={styles.infoLabel}>
                          This will be displayed on Tvs & Tablets
                        </span>
                      </Col>
                      <Col xs={24} md={12} lg={8} xxl={6}>
                        <FormControl
                          required
                          type={INPUT_TYPES.TEXT}
                          isLoading={isFetching}
                          control={FORM_CONTROL_TYPE.INPUT}
                          name={SETTINGS_PROFILE_KEYS.SPECIALITY}
                          label={SETTINGS_PROFILE_LABELS.SPECIALITY}
                        />
                      </Col>
                      <Col xs={24} md={12} lg={8} xxl={6}>
                        <FormControl
                          type={INPUT_TYPES.TEXT}
                          isLoading={isFetching}
                          control={FORM_CONTROL_TYPE.INPUT}
                          name={SETTINGS_PROFILE_KEYS.QUALIFICATION}
                          label={SETTINGS_PROFILE_LABELS.QUALIFICATION}
                        />
                      </Col>

                      <Col xs={24} md={12} lg={8} xxl={6}>
                        <FormControl
                          required
                          type={INPUT_TYPES.TEXT}
                          isLoading={isFetching}
                          control={FORM_CONTROL_TYPE.INPUT}
                          name={SETTINGS_PROFILE_KEYS.MEDICAL_LICENSE}
                          label={SETTINGS_PROFILE_LABELS.MEDICAL_LICENSE_NUMBER}
                        />
                      </Col>
                      <Col xs={24} md={12} lg={8} xxl={6}>
                        <FormControl
                          type={INPUT_TYPES.TEXT}
                          isLoading={isFetching}
                          control={FORM_CONTROL_TYPE.INPUT}
                          name={SETTINGS_PROFILE_KEYS.DEA_LICENSE}
                          label={SETTINGS_PROFILE_LABELS.DEA_LICENSE_NUMBER}
                        />
                      </Col>
                      <Col xs={24} md={12} lg={4} xxl={3}>
                        <FormControl
                          disabled={values?.country_id !== USA_COUNTRY_ID}
                          options={states || []}
                          type={INPUT_TYPES.TEXT}
                          isLoading={isFetching}
                          control={FORM_CONTROL_TYPE.SELECT}
                          name={SETTINGS_PROFILE_KEYS.STATE}
                          label={SETTINGS_PROFILE_LABELS.STATE}
                        />
                      </Col>
                      <Col xs={24} md={12} lg={8} xxl={4}>
                        <FormControl
                          options={countries}
                          isLoading={isFetching}
                          type={INPUT_TYPES.TEXT}
                          control={FORM_CONTROL_TYPE.SELECT}
                          name={SETTINGS_PROFILE_KEYS.COUNTRY}
                          label={SETTINGS_PROFILE_LABELS.COUNTRY}
                        />
                      </Col>
                    </Row>
                  </div>
                </Show>
              </div>
              <div className={styles.parentFormSubmit}>
                <SaveButton disabled={!dirty} loading={isUpdatingProfile || isFetching} />
                <Show when={canRefreshOeToken}>
                  <Button
                    className={styles.parentFormRefreshOeTokenButton}
                    htmlType='button'
                    modifier={BUTTON_MODIFIER.SECONDARY}
                    proportion={BUTTON_PROPORTION.MEDIUM}
                    loading={isRefreshingOeToken}
                    disabled={isRefreshingOeToken}
                    onClick={handleRefreshOeToken}
                    icon={<Icon icon='mdi:refresh' width={18} height={18} />}>
                    Refresh OE token
                  </Button>
                </Show>
              </div>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}
